// eslint-disable-next-line camelcase
import jwt_decode from 'jwt-decode';
import { makeAutoObservable } from 'mobx';
import localStorage from '@repositories/browserStorage/localStorage/LocalStorage';
import sessionStorage from '@repositories/browserStorage/sessionStorage/SessionStorage';
import { TenantModel } from '@repositories/tenantRepository/models/TenantModel';
import type { Tenant } from '@repositories/tenantRepository/tenant.types';
import type { AdditionalPermission, Permission, TenantRole } from '@repositories/userRepository/user.types';
import { ADMIN_CONSOLE_ACCESS_ROLES, FULL_MENU_ACCESS_ROLES } from '@repositories/userRepository/user.types';
import { jwtUtil } from '@utils/jwtUtil';

// 구독 관리자 기능 활성화 여부 (환경변수 기반 모듈 레벨 상수)
// Manager.tsx 등 다른 파일에서도 사용하므로 export
export const IS_SUBSCRIPTION_MANAGER_ENABLED = import.meta.env.VITE_ENABLE_SUBSCRIPTION_MANAGER !== 'false';

type JWT = {
  sub: string;
  id: string;
  roles: string[];
  ap: string[];
  iat: number;
  exp: number;
  mfa: boolean;
};

export type AuthTenant = {
  id: string;
  permission: Permission;
  type: string;
};

class AuthStore {
  private _isLogin: boolean;
  private _id: string;
  private _name: string;
  private _mfa: boolean;
  private _curTenant: AuthTenant;
  private _ap: AdditionalPermission[];
  private _roles: string[];
  private _isLimitedToken: boolean;
  private _managerTenantInfo?: TenantModel;
  private _isInitialized: boolean;

  constructor() {
    this._isLogin = false;
    this._id = '';
    this._name = '';
    this._mfa = false;
    this._curTenant = {
      id: '',
      permission: 'USER',
      type: 'TENANT',
    };
    this._ap = [];
    this._roles = [];
    this._isLimitedToken = false;
    this._managerTenantInfo = undefined;
    this._isInitialized = false;

    // 새 탭에서 localStorage를 통해 전달받은 인증 정보를 sessionStorage로 복원
    this.restoreAuthFromTransfer();

    // 토큰이 있으면 미리 로그인 상태 설정 (withLogin 첫 렌더 리다이렉트 방지)
    const tk = sessionStorage.get('tk');
    if (tk) {
      this._isLogin = true;
    }

    makeAutoObservable<AuthStore>(this);
  }

  private readonly clear = () => {
    this._isLogin = false;
    this._id = '';
    this._name = '';
    this._mfa = false;
    this._curTenant = {
      id: '',
      permission: 'USER',
      type: 'TENANT',
    };
    this._ap = [];
    this._roles = [];
    this._isLimitedToken = false;
    this._isInitialized = false;
  };

  /**
   * localStorage의 authTransfer 데이터를 sessionStorage로 복원
   * 새 탭으로 열 때 sessionStorage가 공유되지 않는 브라우저 대응
   */
  /**
   * JWT 토큰에서 roles를 파싱하여 반환
   */
  private parseRolesFromToken = (token: string): string[] => {
    try {
      const decoded: JWT = jwt_decode(token);
      return decoded.roles ?? [];
    } catch {
      return [];
    }
  };

  private restoreAuthFromTransfer = () => {
    const existingTk = sessionStorage.get('tk');
    if (existingTk) return;

    const transferData = localStorage.get('authTransfer');
    if (!transferData || typeof transferData !== 'string') return;

    try {
      const parsed: Record<string, unknown> = JSON.parse(transferData);
      const tk = parsed.tk;
      const authInfo = parsed.authInfo;
      if (typeof tk === 'string' && typeof authInfo === 'string') {
        sessionStorage.set('tk', tk);
        sessionStorage.set('authInfo', authInfo);
      }
    } catch {
      // 파싱 실패 시 무시
    } finally {
      localStorage.removeItem('authTransfer');
    }
  };

  /**
   * 컴포넌트 최상단에서 호출하여 토큰의 정보를 authStore에 받아오는 함수
   * 새로고침 등의 이유로 화면이 다시 그려질 때 로그인 상태를 유지하기 위함
   * @param curTenantId 어드민 페이지인 경우 사용. 어느 테넌트의 정보를 표시하는지 구분하는데 사용
   * @param isAdmin 슈퍼 어드민인지 아니면 어드민/유저인지 구분하는데 사용
   * @param checkAdminPermission updateCurTenant 에서 매니저 router에서 호출될 때 사용. 해당 테넌트에 접근할 수 있는 권한이 있는지 체크
   */
  public initialize = (curTenantId?: string, isAdmin = false, checkAdminPermission = false) => {
    const authInfo = sessionStorage.get('authInfo');
    const parsedAuthInfo = authInfo && typeof authInfo === 'string' ? JSON.parse(authInfo) : null;
    this._id = parsedAuthInfo?.id ?? '';
    this._name = parsedAuthInfo?.name ?? '';

    const tk = sessionStorage.get('tk');
    if (tk) {
      this._isLogin = true;
      const decoded: JWT = jwt_decode(tk as string);

      if (!decoded.mfa) {
        sessionStorage.removeItem('tk');
        sessionStorage.removeItem('authInfo');
        this.clear();
        return;
      }

      this._ap = jwtUtil.parseAp(decoded.ap);
      this._roles = this.parseRolesFromToken(tk as string);
      this._isInitialized = true;

      // AP가 비어있으면 제한 토큰 (테넌트 선택 필요)
      if (decoded.ap.length === 0) {
        this._isLimitedToken = true;
        return;
      }

      // 풀 토큰: 이전 세션의 제한 토큰 상태 초기화
      this._isLimitedToken = false;

      if (!isAdmin) {
        this.updateCurTenant(curTenantId ?? parsedAuthInfo?.tenantId, checkAdminPermission);
        if (!this.curTenant?.id) {
          // 240216 hsj. 토큰을 authStore와 연동하는 과정이기 때문에 굳이 sessionStorage에서 토큰을 지워주진 않음
          this.clear();
        }
      }
    }
  };

  public get isLogin(): boolean {
    return this._isLogin;
  }

  public get isLimitedToken(): boolean {
    return this._isLimitedToken;
  }

  /**
   * AuthStore가 초기화되었는지 여부
   * - initialize 메서드가 호출되고 JWT 파싱이 완료되면 true
   */
  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * 제한 토큰으로 로그인 (테넌트 미확정 상태)
   * - AP가 빈 배열이고, 테넌트 선택이 필요한 상태
   */
  public loginWithLimitedToken(id: string, name: string) {
    this._isLogin = true;
    this._id = id;
    this._name = name;
    this._ap = [];
    this._isLimitedToken = true;
    sessionStorage.set('authInfo', JSON.stringify({ id, name }));
  }

  /**
   * 제한 토큰 → 풀 토큰으로 전환 (테넌트 선택 완료 후)
   * @returns 성공 여부
   */
  public upgradeToFullToken(ap: AdditionalPermission[], curTenantId?: string) {
    this._ap = ap;
    this._isLimitedToken = false;

    // JWT에서 roles 파싱하여 설정
    const tk = sessionStorage.get('tk');
    if (tk && typeof tk === 'string') {
      this._roles = this.parseRolesFromToken(tk);
    }
    this._isInitialized = true;

    try {
      this.updateCurTenant(curTenantId);
    } catch {
      this.logout();
      return false;
    }

    if (this._curTenant?.id) {
      this.postLogin();
    }
    return true;
  }

  /**
   * - 직접 호출하지 말고 useUser::useSignIn에서만 호출할 것
   * - login은 useSignIn을 이용해 처리
   * @returns 로그인 성공 실패 여부
   */
  public login(id: string, name: string, ap: AdditionalPermission[], curTenantId?: string, isAdmin = false) {
    this._isLogin = true;
    this._id = id;
    this._name = name;
    this._ap = ap;
    this._isLimitedToken = false;
    const tk = sessionStorage.get('tk');

    // roles 파싱 (updateCurTenant에서 isSubscriptionOwnerOnly 확인에 필요)
    if (tk && typeof tk === 'string') {
      this._roles = this.parseRolesFromToken(tk);
    }

    if (isAdmin) {
      return true;
    }

    let success = true;
    if (tk) {
      try {
        this.updateCurTenant(curTenantId);
      } catch (error) {
        // 240216 hsj. 로그인에 실패한 것이기 때문에 sessionStorage의 토큰까지 제거
        this.logout();
        success = false;
      }
    }

    if (this._curTenant?.id) {
      this.postLogin();
    }

    return success;
  }

  public postLogin() {
    // 인증 정보 저장
    sessionStorage.set('authInfo', JSON.stringify({ id: this._id, name: this._name, tenantId: this._curTenant?.id }));
  }

  public updateCurTenant(curTenantId?: string, checkAdminPermission?: boolean) {
    if (curTenantId) {
      const curTenant = this.accessibleTenants.find(({ id }) => id === curTenantId);
      if (curTenant) {
        // 권한이 USER일 경우 (ADMIN 권한이 없을 경우) 403 에러
        // 단, 구독 관리자(TENANT_SUBSCRIPTION_OWNER)이고 환경변수가 활성화된 경우 접근 허용
        if (curTenant.permission === 'USER' && !!checkAdminPermission) {
          const canAccessAsSubscriptionOwner = IS_SUBSCRIPTION_MANAGER_ENABLED && this.hasAdminConsoleAccess;

          if (!canAccessAsSubscriptionOwner) {
            throw new Response('접근할 수 있는 권한이 존재하지 않습니다.', {
              status: 403,
              statusText: 'INVALID_USER',
            });
          }
        }
        this._curTenant = curTenant;
      } else {
        // React Router ErrorResponse로 던지기
        throw new Response('접근할 수 있는 권한이 존재하지 않습니다.', {
          status: 403,
          statusText: 'INVALID_TENANT',
        });
      }
    } else if (this.accessibleTenants.length === 1) {
      this._curTenant = this.accessibleTenants[0];
    } else {
      // NOTE. 테넌트 선택 화면으로 넘어가기 위해서 _curTenant를 비워둠
    }
  }

  /**
   * - 직접 호출하지 말고 useUser::useLogout에서만 호출할 것
   * - logout은 useLogout을 이용해 처리
   */
  public logout() {
    sessionStorage.removeItem('tk');
    sessionStorage.removeItem('authInfo');
    sessionStorage.removeItem('sso_login_email');
    sessionStorage.removeItem('sso_registration_id');
    sessionStorage.removeItem('sso_tenant_id');
    this.clear();
  }

  public get id(): string {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get curTenant(): AuthTenant {
    return this._curTenant;
  }

  public get mfa(): boolean {
    return this._mfa;
  }

  public get isAdmin(): boolean {
    return !!this._ap.find(ap => ap.resourceId === this._curTenant.id && ap.permission === 'SUPER_ADMIN');
  }

  public get isItAdmin(): boolean {
    // NOTE. 하나라도 테넌트에 대한 관리자 권한이 있으면 참
    return !!this._ap.find(
      ap =>
        ap.resourceType === 'TENANT' &&
        (ap.permission === 'GENERAL_ADMIN' || ap.permission === 'SUPER_ADMIN' || ap.permission === 'EXTERNAL_ADMIN'),
    );
  }

  public get accessibleTenants() {
    return this._ap
      .filter(ap => ap.resourceType === 'TENANT')
      .map(ap => ({ id: ap.resourceId, permission: ap.permission, type: 'TENANT' }));
  }

  /**
   * JWT roles claim에서 TENANT_ 접두사가 붙은 역할만 반환
   */
  public get tenantRoles(): TenantRole[] {
    return this._roles.filter((role): role is TenantRole => role.startsWith('TENANT_'));
  }

  /**
   * 어드민 콘솔 접근 가능 여부 (roles 기반)
   * - TENANT_SUPER_ADMIN, TENANT_GENERAL_ADMIN, TENANT_EXTERNAL_ADMIN, TENANT_SUBSCRIPTION_OWNER 중 하나라도 있으면 true
   */
  public get hasAdminConsoleAccess(): boolean {
    return this.tenantRoles.some(role => ADMIN_CONSOLE_ACCESS_ROLES.includes(role));
  }

  /**
   * 구독 관리자 전용 여부
   * - TENANT_SUBSCRIPTION_OWNER만 있고, 다른 관리자 역할(SUPER_ADMIN, GENERAL_ADMIN, EXTERNAL_ADMIN)은 없는 경우
   */
  public get isSubscriptionOwnerOnly(): boolean {
    const tenantRoles = this.tenantRoles;
    const hasSubscriptionOwner = tenantRoles.includes('TENANT_SUBSCRIPTION_OWNER');
    const hasOtherAdminRole = tenantRoles.some(role => FULL_MENU_ACCESS_ROLES.includes(role));
    return hasSubscriptionOwner && !hasOtherAdminRole;
  }

  /**
   * 전체 메뉴 접근 가능 여부
   * - TENANT_SUPER_ADMIN, TENANT_GENERAL_ADMIN, TENANT_EXTERNAL_ADMIN 중 하나라도 있으면 true
   * - 구독 관리자만 있는 경우는 false (구독 메뉴만 접근 가능)
   */
  public get hasFullMenuAccess(): boolean {
    return this.tenantRoles.some(role => FULL_MENU_ACCESS_ROLES.includes(role));
  }

  /**
   * 매니저 콘솔 접근 가능 여부 (TopGnb용)
   * - 기존 IT 관리자(AP 기반)인 경우 true
   * - 또는 roles 기반 어드민 콘솔 접근 가능 + 구독 관리자 기능 활성화된 경우 true
   */
  public get canAccessManagerConsole(): boolean {
    if (this.isItAdmin) {
      return true;
    }
    return IS_SUBSCRIPTION_MANAGER_ENABLED && this.hasAdminConsoleAccess;
  }

  setManagerTenantInfo(tenantInfo: TenantModel) {
    this._managerTenantInfo = tenantInfo;
  }

  get managerTenantInfo() {
    return this._managerTenantInfo ?? new TenantModel({} as Tenant);
  }
}

const authStore = new AuthStore();

export default authStore;
